/**
 * Admin Dashboard — system overview, pending verifications, user stats.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';
import { useNotifications } from '../../contexts/NotificationsContext';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    try {
      const res = await adminAPI.getDashboard();
      setDashboard(res.data);
    } catch (err) {
      console.error('Admin dashboard error:', err);
      toast.error(err?.message || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Live updates: new doctor registration pending
  const { on } = useNotifications();
  useEffect(() => on('new_doctor_registration', loadDashboard), [on, loadDashboard]);

  if (loading) {
    return (
      <Layout title="Admin Dashboard" role="admin">
        <div className="space-y-4 p-4">
          {[1,2,3,4].map(i => <div key={i} className="skeleton h-24 rounded-2xl" />)}
        </div>
      </Layout>
    );
  }

  const stats = dashboard?.stats || {};

  return (
    <Layout title="Admin Dashboard" role="admin">
      <div className="p-4 space-y-5 pb-24">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-5 text-white">
          <p className="text-gray-300 text-sm">System Overview</p>
          <h2 className="text-xl font-bold mt-1">CDCS Admin Panel</h2>
          <p className="text-gray-400 text-sm mt-1">Chronic Diseases Consultation System</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="card text-center py-4">
            <p className="text-3xl font-bold text-primary-600">{stats.total_patients || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Total Patients</p>
          </div>
          <div className="card text-center py-4">
            <p className="text-3xl font-bold text-accent-600">{stats.total_doctors || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Total Doctors</p>
          </div>
          <div className="card text-center py-4 border-amber-200 bg-amber-50">
            <p className="text-3xl font-bold text-amber-600">{stats.pending_verifications || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Pending Verification</p>
          </div>
          <div className="card text-center py-4">
            <p className="text-3xl font-bold text-purple-600">{stats.total_consultations || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Consultations</p>
          </div>
          {(stats.unsettled_payments || 0) > 0 && (
            <div className="card text-center py-4 border-green-200 bg-green-50">
              <p className="text-3xl font-bold text-green-600">{stats.unsettled_payments}</p>
              <p className="text-xs text-gray-500 mt-1">Unsettled Payments</p>
            </div>
          )}
          {(stats.overdue_payments || 0) > 0 && (
            <div className="card text-center py-4 border-red-200 bg-red-50">
              <p className="text-3xl font-bold text-red-600">{stats.overdue_payments}</p>
              <p className="text-xs text-gray-500 mt-1">Overdue Payments</p>
            </div>
          )}
        </div>

        {/* Overdue payments alert */}
        {(stats.overdue_payments || 0) > 0 && (
          <button onClick={() => navigate('/admin/payments')} className="w-full bg-red-50 border border-red-300 rounded-2xl p-4 flex items-start gap-3 hover:shadow-md transition-shadow text-left">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-red-800">
                {stats.overdue_payments} payment{stats.overdue_payments > 1 ? 's' : ''} overdue
              </p>
              <p className="text-xs text-red-600 mt-0.5">
                Doctor fees unsettled for more than 3 days. Tap to review.
              </p>
            </div>
            <svg className="w-5 h-5 text-red-400 ml-auto self-center" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Quick Actions */}
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Management</h3>
          <div className="space-y-3">
            <button onClick={() => navigate('/admin/verify-doctors')} className="card w-full flex items-center gap-4 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center relative">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                {(stats.pending_verifications || 0) > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {stats.pending_verifications}
                  </span>
                )}
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-gray-900">Doctor Verification</p>
                <p className="text-sm text-gray-500">{stats.pending_verifications || 0} pending reviews</p>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button onClick={() => navigate('/admin/users')} className="card w-full flex items-center gap-4 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-gray-900">User Management</p>
                <p className="text-sm text-gray-500">Manage patients & doctors</p>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button onClick={() => navigate('/admin/payments')} className="card w-full flex items-center gap-4 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center relative">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {(stats.overdue_payments || 0) > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {stats.overdue_payments}
                  </span>
                )}
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-gray-900">Payments & Settlements</p>
                <p className="text-sm text-gray-500">
                  {(stats.unsettled_payments || 0) > 0
                    ? `${stats.unsettled_payments} unsettled payment${stats.unsettled_payments > 1 ? 's' : ''}`
                    : 'View payments, settle doctor fees'}
                </p>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button onClick={() => navigate('/admin/settings')} className="card w-full flex items-center gap-4 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-gray-900">System Settings</p>
                <p className="text-sm text-gray-500">Platform configuration</p>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
